// @ts-check
/**
 * **盤面の状態の決まり**（Issue #523。`duelEditor/core/` ＝ 舞台の決まりごと）。契約は `types.d.ts`。
 *
 * ## `core/field` と対称
 *
 * | | 答えること | 受け取るもの |
 * |---|---|---|
 * | `core/field` | 盤の**場所**（平面の 4 点） | ゾーンのキー → 4 点 |
 * | `core/board` | 盤に**置かれているもの** | ゾーンのキー → カード / 山 / 枚数 |
 *
 * **duelEditor は再現エンジン（ocgCore）の形を知らない。** 生の `duel_info()` 形や
 * 時系列のスナップショット（uid の配列）から写すのは `editorPorts/duelBoard.js` の仕事で、
 * ここが受け取るのは**ゾーンのキーの表だけ**。Field が `overlay_zones` の形を知らないのと同じ。
 *
 * ## 表に入れてよい 3 つの形
 *
 * - **カード 1 枚**（`monster:0:1` / `field:0` のような 1 枚ぶんの場所）
 * - **カードの配列**（山。**末尾が一番上**）
 * - **数**（枚数だけ分かっていて中身が分からない。デッキ・EX デッキ）
 *
 * `known: false` でも返る（**null を返さない**）。呼ぶ側は `board.known` で判定する。
 */
import { parseZoneKey } from '../field/zones';

/** @typedef {import('./types').BoardState} BoardState */
/** @typedef {import('./types').Card} Card */

/** 契約の口の一覧（`types.d.ts` の `BoardState` と 1 対 1） */
export const BOARD_PORT = Object.freeze(['known', 'keys', 'at', 'pile', 'count', 'byUid', 'cards']);

const str = (v) => (v == null || v === '' ? null : String(v));

/** 表に入れるカードの形を揃える（知らないキーは落とす） */
function toCard(raw) {
  if (!raw || typeof raw !== 'object') return null;
  return Object.freeze({
    uid: str(raw.uid),
    code: str(raw.code),
    faceUp: raw.faceUp !== false,
    defense: raw.defense === true,
    stats: raw.stats ? Object.freeze({ ...raw.stats }) : null,
  });
}

/**
 * 盤面の状態を作る。**どの写し替えもここを通る**ので口が欠けない。
 *
 * @param {object} [opts]
 * @param {Record<string, any>} [opts.zones] ゾーンのキー → カード / 山（末尾が一番上）/ 枚数
 * @returns {BoardState}
 */
export function toBoardState(opts = {}) {
  const zones = opts?.zones ?? {};
  /** @type {Record<string, any>} */
  const table = {};
  Object.entries(zones ?? {}).forEach(([key, value]) => {
    if (!parseZoneKey(key)) return;   // **知らないキーは落とす**（語彙は core/field/zones.js）
    if (Array.isArray(value)) {
      const list = value.map(toCard).filter(Boolean);
      if (list.length) table[key] = Object.freeze(list);
      return;
    }
    if (typeof value === 'number') {
      if (value > 0) table[key] = value;   // 枚数だけ
      return;
    }
    const card = toCard(value);
    if (card) table[key] = card;
  });

  const keys = Object.freeze(Object.keys(table));
  /** 全部のカード（山は下から順）。`byUid` の索引にも使う */
  const all = [];
  keys.forEach((zone) => {
    const v = table[zone];
    if (typeof v === 'number') return;
    if (Array.isArray(v)) v.forEach((card, index) => all.push(Object.freeze({ zone, card, index })));
    else all.push(Object.freeze({ zone, card: v, index: null }));
  });
  const byUidIndex = new Map(all.filter((e) => e.card.uid).map((e) => [e.card.uid, e]));

  return Object.freeze({
    known: keys.length > 0,
    keys,
    /** 1 枚ぶんの場所のカード（山や枚数だけの場所は null） */
    at: (key) => {
      const v = table[key];
      return v && !Array.isArray(v) && typeof v !== 'number' ? v : null;
    },
    /** 山（**末尾が一番上**）。1 枚ぶんの場所はその 1 枚、枚数だけの場所は空 */
    pile: (key) => {
      const v = table[key];
      if (Array.isArray(v)) return v;
      if (!v || typeof v === 'number') return EMPTY_LIST;
      return Object.freeze([v]);
    },
    /** 枚数（中身が分からない場所もここは答える） */
    count: (key) => {
      const v = table[key];
      if (typeof v === 'number') return v;
      if (Array.isArray(v)) return v.length;
      return v ? 1 : 0;
    },
    /** uid から居場所を引く（**演出がカードを指すのに使う**）。無ければ null */
    byUid: (uid) => byUidIndex.get(str(uid)) ?? null,
    /** 置いてあるカードぜんぶ（山は下から順） */
    cards: () => all,
  });
}

const EMPTY_LIST = /** @type {readonly any[]} */ (Object.freeze([]));

/** 盤面が分からないときの状態（**null を返さない**ので、呼ぶ側は `known` で判定する） */
export const EMPTY_BOARD = toBoardState();

/*
 * ---- カードの面（Issue #766）----
 *
 * **中身の分からないカード（`code` が無い）は裏＝そのプレイヤーのデッキのスリーブで描き、名前を出さない**。
 * 観戦の視点でルームから届くイベント・盤面では、伏せたカードは `code: null`・`hidden: true` で来る（中身はそもそも届かない）。
 * 盤だけでなく、カードを見せる演出（カットイン・パネル）もここを通して絵を引く —— 「表で見せるか」を演出ごとに書かない。
 * 公開された瞬間（confirm_cards など）はそのイベントに `code` が載って来るので表になり、手札に戻れば盤面の `code` がまた null でスリーブ。
 * 空のゾーン（置いていない）とは別: 置いてあるカードは uid を持ち、`code` だけが null。
 */

/** そのカードの中身が伏せてあるか（`code` が無い） */
export const isHiddenCode = (/** @type {any} */ code) => code == null || code === '';

/**
 * カードの面の絵。表で中身が分かれば `cards.image(code)`、裏か中身が分からなければ `cards.sleeve(player)`。
 * 絵がまだ読めていなければ null（テーマが地の色で描く）。
 *
 * @param {import('./types').CardAccess | null | undefined} cards 素材の引き方（`scene.cards` / `ctx.cards`）
 * @param {{ code?: any, player?: number, faceUp?: boolean }} card
 */
export function cardFaceImage(cards, { code = null, player = 0, faceUp = true } = {}) {
  if (faceUp && !isHiddenCode(code)) return cards?.image?.(code) ?? null;
  return cards?.sleeve?.(player === 1 ? 1 : 0) ?? null;
}

/**
 * カードの名前。中身が分からなければ空（**伏せたカードの名前は出さない**）。名前が引けなければ空。
 *
 * @param {import('./types').CardAccess | null | undefined} cards
 * @param {any} code
 */
export const cardLabel = (cards, code) => (isHiddenCode(code) ? '' : (cards?.name?.(code) ?? ''));
